import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Wallet, Gift, Share2, Copy,
  ChevronRight, FileText, Shield, LifeBuoy, Trash2, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import MobileNav from '../components/MobileNav';
import LegalLink from '../components/LegalLink';
import DeleteAccountDialog from '../components/DeleteAccountDialog';
import { apiFetch } from '../lib/apiClient';

export default function Profile() {
  const { user, logout, checkAuth } = useAuth();
  const [referral, setReferral] = useState(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchReferral = useCallback(async () => {
    if (!user?.phone_verified) {
      setReferral(null);
      return;
    }
    try {
      const response = await apiFetch('/api/referral');
      if (response.ok) {
        setReferral(await response.json());
      }
    } catch (error) {
      console.error('Failed to load referral:', error);
    }
  }, [user?.phone_verified]);

  useEffect(() => {
    fetchReferral();
  }, [fetchReferral]);

  const shareReferral = async () => {
    const code = referral?.referral_code;
    if (!code) return;
    const message = `Join me on SkipdQ and we both get CA$1 wallet credit! Use my referral code ${code}.`;
    try {
      if (Capacitor.isNativePlatform()) {
        const { Share } = await import('@capacitor/share');
        await Share.share({ title: 'SkipdQ referral', text: message });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: 'SkipdQ referral', text: message });
        return;
      }
      await navigator.clipboard.writeText(message);
      toast.success('Referral message copied to clipboard');
    } catch (error) {
      if (error?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(code);
          toast.success('Referral code copied');
        } catch {
          toast.error('Could not share referral code');
        }
      }
    }
  };

  const copyCode = async () => {
    if (!referral?.referral_code) return;
    try {
      await navigator.clipboard.writeText(referral.referral_code);
      toast.success('Referral code copied');
    } catch {
      toast.error('Could not copy code');
    }
  };

  const handleRedeem = async () => {
    const code = redeemCode.trim();
    if (!code) {
      toast.error('Enter a referral code');
      return;
    }
    setRedeeming(true);
    try {
      const response = await apiFetch('/api/referral/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Could not redeem code');
      }
      toast.success('Referral applied — CA$1 added to your wallet!');
      setRedeemCode('');
      await Promise.all([fetchReferral(), checkAuth()]);
    } catch (error) {
      toast.error(error.message || 'Could not redeem code');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      
      <div className="max-w-lg mx-auto px-4 pt-20">
        <Link 
          to="/" 
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl p-6 shadow-sm mb-8"
        >
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xl font-bold text-primary">
                  {user?.name?.charAt(0) || '?'}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold" data-testid="user-name">{user?.name}</h1>
              <p className="text-muted-foreground text-sm">{user?.email}</p>
              <Badge variant="secondary" className="mt-1 capitalize">
                {user?.role}
              </Badge>
            </div>
          </div>
          
          {user?.phone_verified && (
            <Link to="/wallet" className="block mt-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors">
                <div className="flex items-center gap-3">
                  <Wallet className="w-5 h-5 text-primary" />
                  <div className="text-left">
                    <p className="font-medium text-sm">SkipdQ Wallet</p>
                    <p className="text-xs text-muted-foreground">
                      Balance: CA$ {(user?.wallet_balance ?? 0).toFixed(2)}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-primary">Manage</span>
              </div>
            </Link>
          )}

          {user?.phone_verified && referral && (
            <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-primary" />
                <p className="font-medium text-sm">Refer &amp; earn</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Share your code. You both get CA$1 wallet credit when a friend completes their first order.
              </p>
              {referral.paused && (
                <p className="text-xs font-medium text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3" data-testid="referral-paused-note">
                  Referral rewards are currently paused. Codes cannot be redeemed right now.
                </p>
              )}

              {referral.referral_code && (
                <div className="flex items-center gap-2 mb-3">
                  <button
                    type="button"
                    onClick={copyCode}
                    className="flex-1 flex items-center justify-between rounded-xl bg-background border px-3 py-2 text-left"
                    data-testid="referral-code"
                  >
                    <span className="font-mono font-semibold tracking-wider">{referral.referral_code}</span>
                    <Copy className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <Button onClick={shareReferral} className="rounded-xl h-10" data-testid="share-referral-btn">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{referral.referrals_count || 0} friends referred</span>
                <span className="font-medium text-primary">CA$ {(referral.earnings ?? 0).toFixed(2)} earned</span>
              </div>

              {!referral.referred && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-2">Have a code? Redeem it for CA$1.</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={redeemCode}
                      onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 rounded-xl bg-background border px-3 py-2 text-base font-mono tracking-wider"
                      data-testid="redeem-code-input"
                    />
                    <Button
                      variant="outline"
                      className="rounded-xl h-10"
                      disabled={redeeming || referral.paused}
                      onClick={handleRedeem}
                      data-testid="redeem-code-btn"
                    >
                      {redeeming ? 'Applying…' : 'Redeem'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button 
            onClick={logout}
            className="w-full mt-6 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            Sign Out
          </button>
        </motion.div>

        <h2 className="text-lg font-bold mb-4">Orders</h2>
        <div className="bg-card rounded-2xl shadow-sm divide-y" data-testid="orders-section">
          <Link to="/orders" className={SETTINGS_ROW_CLASS}>
            <SettingsRowContent icon={ClipboardList} label="View orders & pickup status" />
          </Link>
        </div>

        <h2 className="text-lg font-bold mt-10 mb-4">Legal &amp; support</h2>
        <div className="bg-card rounded-2xl shadow-sm divide-y" data-testid="legal-support-section">
          <LegalLink kind="privacy" className={SETTINGS_ROW_CLASS}>
            <SettingsRowContent icon={Shield} label="Privacy Policy" />
          </LegalLink>
          <LegalLink kind="terms" className={SETTINGS_ROW_CLASS}>
            <SettingsRowContent icon={FileText} label="Terms of Service" />
          </LegalLink>
          <Link to="/help" className={SETTINGS_ROW_CLASS}>
            <SettingsRowContent icon={LifeBuoy} label="Help & support" />
          </Link>
        </div>

        <h2 className="text-lg font-bold mt-10 mb-4">Account</h2>
        <div className="bg-card rounded-2xl shadow-sm p-4 mb-10" data-testid="account-section">
          <p className="text-sm text-muted-foreground mb-3">
            Permanently delete your account and personal data. Any wallet balance is forfeited.
          </p>
          <Button
            variant="outline"
            className="w-full rounded-xl text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete account
          </Button>
        </div>
      </div>

      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
      <MobileNav />
    </div>
  );
}

const SETTINGS_ROW_CLASS =
  'w-full flex items-center justify-between px-4 py-3 text-left text-sm hover:bg-muted/50 transition-colors first:rounded-t-2xl last:rounded-b-2xl';

function SettingsRowContent({ icon: Icon, label }) {
  return (
    <>
      <span className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {label}
      </span>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </>
  );
}
